import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Clock3, KeyRound, Mail, RefreshCw, ShieldCheck, UserPlus, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  activateCreatorRegistrationRequest,
  CreatorRegistrationRequest,
  decideCreatorRegistrationRequest,
  listCreatorRegistrationRequests,
  listLegacyOrganizations,
} from '../services/creatorRegistrationService';
import { LegacyOrganization } from '../types/legacy';
import { createRegisteredAdminUser, CreateRegisteredAdminUserPayload } from '../services/registeredUsersService';

const CreatorRequests = () => {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'activated' | 'rejected'>('pending');
  const [requests, setRequests] = useState<CreatorRegistrationRequest[]>([]);
  const [allRequests, setAllRequests] = useState<CreatorRegistrationRequest[]>([]);
  const [organizations, setOrganizations] = useState<LegacyOrganization[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [createNewOrganization, setCreateNewOrganization] = useState(false);
  const [createForm, setCreateForm] = useState<CreateRegisteredAdminUserPayload>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    temporaryPassword: '',
    organizationId: 0,
    organizationName: '',
    createOrganization: false,
    note: '',
  });

  const canApprove = isSuperAdmin;

  const loadAllAndFiltered = async (filter = statusFilter) => {
    try {
      setLoading(true);
      setError(null);

      // I counter devono rappresentare la situazione globale,
      // mentre l'elenco sotto rispetta il filtro corrente.
      const [allRes, filteredRes, organizationsRes] = await Promise.allSettled([
        listCreatorRegistrationRequests('all'),
        listCreatorRegistrationRequests(filter),
        listLegacyOrganizations(),
      ]);

      if (allRes.status === 'fulfilled') {
        setAllRequests(allRes.value.requests || []);
      } else {
        setAllRequests([]);
      }

      if (filteredRes.status === 'fulfilled') {
        setRequests(filteredRes.value.requests || []);
      } else {
        setRequests([]);
      }

      if (organizationsRes.status === 'fulfilled') {
        setOrganizations(organizationsRes.value || []);
      } else {
        setOrganizations([]);
      }
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail
        ? `Impossibile caricare le richieste EDU: ${detail}`
        : 'Impossibile caricare le richieste EDU. Verifica i permessi superadmin.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canApprove) return;
    void loadAllAndFiltered(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, canApprove]);

  const counters = useMemo(() => {
    const base = { all: allRequests.length, pending: 0, approved: 0, activated: 0, rejected: 0 };
    for (const request of allRequests) {
      if (request.status === 'pending') base.pending += 1;
      if (request.status === 'approved') base.approved += 1;
      if (request.status === 'activated') base.activated += 1;
      if (request.status === 'rejected') base.rejected += 1;
    }
    return base;
  }, [allRequests]);

  const decide = async (requestId: string, decision: 'approved' | 'rejected') => {
    const note = window.prompt(
      decision === 'approved'
        ? 'Nota approvazione (opzionale):'
        : 'Motivazione rifiuto (opzionale):',
      '',
    ) || '';

    try {
      setUpdatingId(requestId);
      setError(null);
      await decideCreatorRegistrationRequest({ requestId, decision, note });
      setSuccess(decision === 'approved' ? 'Richiesta approvata.' : 'Richiesta rifiutata.');
      setTimeout(() => setSuccess(null), 2500);
      await loadAllAndFiltered(statusFilter);
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail
        ? `Operazione non completata: ${detail}`
        : 'Operazione non completata. Controlla i permessi o la configurazione Glooci Platform.');
    } finally {
      setUpdatingId(null);
    }
  };

  const activate = async (requestItem: CreatorRegistrationRequest) => {
    const temporaryPassword =
      (window.prompt('Password iniziale (lascia vuoto per generazione automatica):', '') || '').trim();
    const note = (window.prompt('Nota attivazione (opzionale):', '') || '').trim();
    const sortedOrganizations = [...organizations].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), 'it', { sensitivity: 'base' }));
    const organizationPromptHint = sortedOrganizations
      .slice(0, 15)
      .map((item) => `${item.id}:${item.name}`)
      .join(' | ');
    const organizationIdRaw = (window.prompt(
      `ID organizzazione (opzionale). Esempi disponibili: ${organizationPromptHint || 'nessuna'}\nLascia vuoto per usare/creare da nome.`,
      '',
    ) || '').trim();
    const organizationId = organizationIdRaw ? Number(organizationIdRaw) : NaN;
    const hasOrganizationId = Number.isFinite(organizationId) && organizationId > 0;
    const organizationName = !hasOrganizationId
      ? (window.prompt(
        'Nome organizzazione (opzionale). Vuoto = usa organizzazione richiesta.',
        requestItem.organization || '',
      ) || '').trim()
      : '';

    const shouldCreateOrganization = !hasOrganizationId && Boolean(organizationName)
      ? window.confirm(
        'Se il nome non esiste verra creata una nuova organizzazione. Confermi questa opzione?',
      )
      : false;

    try {
      setUpdatingId(requestItem.id);
      setError(null);
      await activateCreatorRegistrationRequest({
        requestId: requestItem.id,
        note,
        temporaryPassword,
        organizationId: hasOrganizationId ? organizationId : null,
        organizationName: organizationName || requestItem.organization || '',
        createOrganization: shouldCreateOrganization,
      });
      setSuccess('Utenza attivata con successo.');
      setTimeout(() => setSuccess(null), 2500);
      await loadAllAndFiltered(statusFilter);
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail
        ? `Attivazione non completata: ${detail}`
        : 'Attivazione non completata. Verifica permessi e connettivita API.');
    } finally {
      setUpdatingId(null);
    }
  };

  const openCreateAdminModal = () => {
    const firstOrganization = organizations
      .map((item) => ({
        id: Number(item?.id || 0),
        name: String(item?.name || '').trim(),
      }))
      .find((item) => item.id > 0 && item.name);
    setCreateForm({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      temporaryPassword: '',
      organizationId: firstOrganization?.id || 0,
      organizationName: firstOrganization?.name || '',
      createOrganization: false,
      note: '',
    });
    setCreateNewOrganization(false);
    setCreateModalOpen(true);
  };

  const submitCreateAdmin = async () => {
    const payload: CreateRegisteredAdminUserPayload = {
      username: String(createForm.username || '').trim(),
      email: String(createForm.email || '').trim().toLowerCase(),
      firstName: String(createForm.firstName || '').trim(),
      lastName: String(createForm.lastName || '').trim(),
      temporaryPassword: String(createForm.temporaryPassword || '').trim(),
      organizationId: Number(createForm.organizationId || 0),
      organizationName: String(createForm.organizationName || '').trim(),
      createOrganization: Boolean(createForm.createOrganization),
      note: String(createForm.note || '').trim(),
    };
    if (!payload.username || !payload.email || !payload.firstName || !payload.lastName || !payload.temporaryPassword) {
      setError('Compila tutti i campi obbligatori per creare l\'admin.');
      return;
    }
    if (payload.createOrganization) {
      if (!payload.organizationName) {
        setError('Inserisci il nome della nuova organizzazione.');
        return;
      }
      payload.organizationId = undefined;
    } else if (!Number.isFinite(Number(payload.organizationId || 0)) || Number(payload.organizationId || 0) <= 0) {
      setError('Seleziona un\'organizzazione valida.');
      return;
    }
    try {
      setCreatingAdmin(true);
      setError(null);
      await createRegisteredAdminUser(payload);
      setCreateModalOpen(false);
      setSuccess('Nuovo admin organizzazione creato con successo.');
      setTimeout(() => setSuccess(null), 2500);
      await loadAllAndFiltered(statusFilter);
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail || 'Creazione admin non riuscita.');
    } finally {
      setCreatingAdmin(false);
    }
  };

  if (!canApprove) {
    return (
      <div className="card p-8 max-w-3xl">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2 mb-2">
          <ShieldCheck size={24} className="text-primary" />
          Richieste EDU
        </h1>
        <p className="text-sm text-text-secondary">
          Questa sezione e riservata al superadmin.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
            <UserPlus size={30} className="text-primary" />
            Richieste EDU e onboarding assistito
          </h1>
          <p className="text-text-secondary mt-1">
            Gestisci le richieste manuali per scuole, universita e gli eventuali flussi legacy ancora presenti.
          </p>
        </div>
        <button type="button" className="btn-outline inline-flex items-center gap-2" onClick={() => void loadAllAndFiltered(statusFilter)}>
          <RefreshCw size={15} /> Aggiorna
        </button>
        <button
          type="button"
          className="btn-primary inline-flex items-center gap-2"
          onClick={openCreateAdminModal}
          disabled={creatingAdmin}
        >
          <UserPlus size={15} /> Nuovo admin organizzazione
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card p-4">
          <p className="text-xs text-text-secondary">Totali</p>
          <p className="text-2xl font-bold text-text-primary">{counters.all}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-secondary">In attesa</p>
          <p className="text-2xl font-bold text-amber-600">{counters.pending}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-secondary">Approvate</p>
          <p className="text-2xl font-bold text-green-600">{counters.approved}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-secondary">Attivate</p>
          <p className="text-2xl font-bold text-blue-600">{counters.activated}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-secondary">Rifiutate</p>
          <p className="text-2xl font-bold text-red-600">{counters.rejected}</p>
        </div>
      </div>

      <div className="card p-5 flex flex-wrap items-center gap-2">
        {(['pending', 'approved', 'activated', 'rejected', 'all'] as const).map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              statusFilter === status ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary hover:border-primary/40'
            }`}
          >
            {
              status === 'all'
                ? 'Tutte'
                : status === 'pending'
                  ? 'In attesa'
                  : status === 'approved'
                    ? 'Approvate'
                    : status === 'activated'
                      ? 'Attivate'
                      : 'Rifiutate'
            }
          </button>
        ))}
      </div>

      {error && <div className="rounded-lg border border-error/30 bg-error/10 p-4 text-error">{error}</div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-700">{success}</div>}

      <div className="space-y-3">
        {loading ? (
          <div className="card p-6 text-text-secondary">Caricamento richieste...</div>
        ) : requests.length === 0 ? (
          <div className="card p-8 text-center text-text-secondary">Nessuna richiesta per il filtro selezionato.</div>
        ) : (
          requests.map((item) => (
            <article key={item.id} className="card p-5 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-text-primary">{item.fullName}</span>
                <span className="text-xs text-text-secondary">@{item.username}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                  item.requestType === 'creator'
                    ? 'bg-slate-100 text-slate-700'
                    : 'bg-indigo-100 text-indigo-700'
                }`}>
                  {item.requestType === 'creator' ? 'Legacy creator' : 'EDU'}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ml-auto ${
                  item.status === 'pending'
                    ? 'bg-amber-100 text-amber-700'
                    : item.status === 'approved'
                      ? 'bg-green-100 text-green-700'
                      : item.status === 'activated'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-red-100 text-red-700'
                }`}>
                  {
                    item.status === 'pending'
                      ? 'In attesa'
                      : item.status === 'approved'
                        ? 'Approvata'
                        : item.status === 'activated'
                          ? 'Attivata'
                          : 'Rifiutata'
                  }
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <p className="text-text-secondary inline-flex items-center gap-1">
                  <Mail size={14} /> {item.email}
                </p>
                <p className="text-text-secondary">Organizzazione: {item.organization || 'n/d'}</p>
                <p className="text-text-secondary">Creata: {item.createdAt ? new Date(item.createdAt).toLocaleString('it-IT') : 'n/d'}</p>
                <p className="text-text-secondary">Revisionata da: {item.reviewedBy || 'n/d'}</p>
                <p className="text-text-secondary">Attivata da: {item.activatedBy || 'n/d'}</p>
                <p className="text-text-secondary">Attivata il: {item.activatedAt ? new Date(item.activatedAt).toLocaleString('it-IT') : 'n/d'}</p>
                <p className="text-text-secondary">Org attivazione: {item.activatedOrganizationName || 'n/d'}</p>
              </div>

              {item.notes && (
                <div className="rounded-lg border border-border bg-background p-3 text-sm text-text-primary">
                  <p className="font-medium mb-1">
                    {item.requestType === 'creator' ? 'Motivazione richiesta' : 'Motivazione educativa'}
                  </p>
                  <p>{item.notes}</p>
                </div>
              )}

              {item.reviewNote && (
                <div className="rounded-lg border border-border bg-background p-3 text-sm text-text-primary">
                  <p className="font-medium mb-1">Nota revisione</p>
                  <p>{item.reviewNote}</p>
                </div>
              )}

              {item.status === 'pending' && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    type="button"
                    className="btn-primary inline-flex items-center gap-1 text-sm"
                    disabled={updatingId === item.id}
                    onClick={() => void decide(item.id, 'approved')}
                  >
                    <CheckCircle2 size={14} /> Approva
                  </button>
                  <button
                    type="button"
                    className="btn-outline inline-flex items-center gap-1 text-sm"
                    disabled={updatingId === item.id}
                    onClick={() => void decide(item.id, 'rejected')}
                  >
                    <XCircle size={14} /> Rifiuta
                  </button>
                  {updatingId === item.id && (
                    <span className="text-xs text-text-secondary inline-flex items-center gap-1">
                      <Clock3 size={12} /> Aggiornamento...
                    </span>
                  )}
                </div>
              )}

              {item.status === 'approved' && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    type="button"
                    className="btn-primary inline-flex items-center gap-1 text-sm"
                    disabled={updatingId === item.id}
                    onClick={() => void activate(item)}
                  >
                    <KeyRound size={14} /> Attiva utenza
                  </button>
                  {updatingId === item.id && (
                    <span className="text-xs text-text-secondary inline-flex items-center gap-1">
                      <Clock3 size={12} /> Attivazione...
                    </span>
                  )}
                </div>
              )}
            </article>
          ))
        )}
      </div>

      {createModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-border">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h3 className="text-lg font-semibold text-text-primary">Crea admin organizzazione</h3>
              <button type="button" className="btn-outline text-xs" onClick={() => setCreateModalOpen(false)} disabled={creatingAdmin}>
                Chiudi
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div className="grid gap-3 md:grid-cols-2">
                <input className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Username *" value={createForm.username} onChange={(e) => setCreateForm((prev) => ({ ...prev, username: e.target.value }))} disabled={creatingAdmin} />
                <input type="email" className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Email *" value={createForm.email} onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))} disabled={creatingAdmin} />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <input className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Nome *" value={createForm.firstName} onChange={(e) => setCreateForm((prev) => ({ ...prev, firstName: e.target.value }))} disabled={creatingAdmin} />
                <input className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Cognome *" value={createForm.lastName} onChange={(e) => setCreateForm((prev) => ({ ...prev, lastName: e.target.value }))} disabled={creatingAdmin} />
              </div>
              <input type="password" className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Password temporanea *" value={createForm.temporaryPassword} onChange={(e) => setCreateForm((prev) => ({ ...prev, temporaryPassword: e.target.value }))} disabled={creatingAdmin} />
              {!createNewOrganization ? (
                <select
                  className="w-full rounded-xl border border-border bg-white px-3 py-2"
                  value={createForm.organizationId || 0}
                  onChange={(e) => {
                    const selectedId = Number(e.target.value || 0);
                    const selectedOrganization = organizations.find((org) => Number(org?.id || 0) === selectedId);
                    setCreateForm((prev) => ({
                      ...prev,
                      organizationId: selectedId,
                      organizationName: String(selectedOrganization?.name || ''),
                      createOrganization: false,
                    }));
                  }}
                  disabled={creatingAdmin}
                >
                  <option value={0}>Seleziona organizzazione *</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="w-full rounded-xl border border-border bg-white px-3 py-2"
                  placeholder="Nome nuova organizzazione *"
                  value={createForm.organizationName || ''}
                  onChange={(e) => setCreateForm((prev) => ({
                    ...prev,
                    organizationId: undefined,
                    organizationName: e.target.value,
                    createOrganization: true,
                  }))}
                  disabled={creatingAdmin}
                />
              )}
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => {
                  setCreateNewOrganization((prev) => !prev);
                  setCreateForm((prev) => ({
                    ...prev,
                    organizationId: undefined,
                    organizationName: '',
                    createOrganization: !createNewOrganization,
                  }));
                }}
                disabled={creatingAdmin}
              >
                {createNewOrganization ? 'Usa organizzazione esistente' : 'Organizzazione non in lista? Creane una nuova'}
              </button>
              <textarea className="w-full rounded-xl border border-border bg-white px-3 py-2" rows={3} placeholder="Nota interna (opzionale)" value={createForm.note || ''} onChange={(e) => setCreateForm((prev) => ({ ...prev, note: e.target.value }))} disabled={creatingAdmin} />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
              <button type="button" className="btn-outline" onClick={() => setCreateModalOpen(false)} disabled={creatingAdmin}>Annulla</button>
              <button type="button" className="btn-primary" onClick={() => void submitCreateAdmin()} disabled={creatingAdmin}>
                {creatingAdmin ? 'Creazione...' : 'Crea admin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorRequests;
